import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// Optional pretty names for subdomains
const tenantNameMap = {
  // acme: "ACME Rentals",
};

const isProdHost = (host) => /webtrix24\.com$/i.test(host);

const getTenantFromHost = (host) => {
  const h = host.split(":")[0];
  const parts = h.split(".");
  if (parts.length >= 3) {
    const sub = parts[0].toLowerCase();
    if (sub !== "www") return sub;
  }
  return null;
};

const prettyTenantName = (sub) =>
  (sub && tenantNameMap[sub]) ||
  (sub ? sub.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null);

export default function SeoNative({ fallback }) {
  const { pathname, search } = useLocation();
  const [seo, setSeo] = useState(null);

  const { origin, host } = window.location;
  const tenantSub = useMemo(() => getTenantFromHost(host), [host]);
  const tenantLabel = useMemo(() => prettyTenantName(tenantSub), [tenantSub]);

  // Base, tenant-aware fallback
  const baseFallback = useMemo(() => {
    const siteName = "Webtrix24";
    const baseTitle = tenantLabel
      ? `${tenantLabel} – Computer Rental CRM`
      : "Webtrix24 Rental – Computer Rental CRM";
    return {
      title: baseTitle,
      description:
        "Manage inventory, invoices, contracts, and returns with Webtrix24 Rental. Multi-branch ready and built for growth.",
      canonical: `${origin}${pathname}${search || ""}`,
      image: `${origin}/og/rental.jpg`,
      keywords: "computer rental software, rental crm, inventory, invoices",
      robots: isProdHost(host) ? "index,follow" : "noindex,nofollow",
      siteName,
    };
  }, [origin, pathname, search, host, tenantLabel]);

  const mergedFallback = useMemo(
    () => ({ ...baseFallback, ...(fallback || {}) }),
    [baseFallback, fallback]
  );

  // Fetch per-route SEO (tenant-aware)
  useEffect(() => {
    let alive = true;
    const slug = pathname || "/";
    fetch(`/api/seo?slug=${encodeURIComponent(slug)}&host=${encodeURIComponent(host)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => {
        if (!alive) return;
        const canonical = json.canonical || `${origin}${pathname}${search || ""}`;
        const robots = json.robots || (isProdHost(host) ? "index,follow" : "noindex,nofollow");
        setSeo({ ...mergedFallback, ...json, canonical, robots });
      })
      .catch(() => alive && setSeo(mergedFallback));
    return () => { alive = false; };
  }, [pathname, search, host, origin, mergedFallback]);

  const data = seo || mergedFallback;
  const { title, description, canonical, image, keywords, robots, siteName } = data;

  // Minimal JSON-LD (switch type if URL contains “rental”)
  const isRental = /rental/i.test(pathname);
  const structuredData = isRental
    ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: title,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description,
        url: canonical,
        image,
        provider: { "@type": "Organization", name: siteName },
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: canonical,
        image,
      };

  const finalTitle = tenantLabel ? `${tenantLabel} | ${title}` : title;

  // React 19 will hoist these into <head>
  return (
    <>
      <title>{finalTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {robots && <meta name="robots" content={robots} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {image && <meta property="og:image" content={image} />}
      <meta property="og:title" content={finalTitle} />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </>
  );
}
